/**
 * ============================================================================
 * WorkbenchFilters — Phase 6e frontend (Equipment & Validation Repository).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: top filter bar (criticality / GAMP class / lifecycle status /
 *          site). Each filter is a single-select dropdown with an explicit
 *          "All" option so users can clear without keyboard juggling.
 *          A free-text site input is supported because sites are tenant
 *          data, not an enum.
 *
 * A11Y: every <select> has a paired <label>; the row is wrapped in a <form>
 *  with role="search" so AT users hear "Filters search region".
 * ============================================================================ */

import { useTranslation } from 'react-i18next';
import styles from './EquipmentWorkbench.module.css';
import type {
  EquipmentCriticality,
  EquipmentLifecycleStatus,
  EquipmentQueryParams,
  GampClass,
} from './useEquipmentQuery';

const CRITICALITY: EquipmentCriticality[] = ['CRITICAL', 'MAJOR', 'MINOR', 'NON_GMP'];
const GAMP: GampClass[] = ['GAMP5', 'GAMP4', 'GAMP3', 'GAMP1'];
const LIFECYCLE: EquipmentLifecycleStatus[] = [
  'PLANNED',
  'URS_APPROVED',
  'DQ_APPROVED',
  'FAT_PASSED',
  'SAT_PASSED',
  'IQ_PASSED',
  'OQ_PASSED',
  'PQ_PASSED',
  'QUALIFIED',
  'RESTRICTED',
  'RETIRED',
  'DECOMMISSIONED',
];

export interface WorkbenchFiltersProps {
  value: EquipmentQueryParams;
  onChange: (next: EquipmentQueryParams) => void;
}

export function WorkbenchFilters({ value, onChange }: WorkbenchFiltersProps) {
  const { t } = useTranslation();

  function update<K extends keyof EquipmentQueryParams>(
    key: K,
    next: EquipmentQueryParams[K] | '',
  ) {
    const merged = { ...value };
    if (next === '' || next == null) {
      delete merged[key];
    } else {
      merged[key] = next as EquipmentQueryParams[K];
    }
    onChange(merged);
  }

  function clear() {
    onChange({});
  }

  const hasAny =
    !!value.criticality ||
    !!value.gamp_class ||
    !!value.lifecycle_status ||
    !!value.site_code ||
    !!value.q;

  return (
    <form
      role="search"
      className={styles.filterBar}
      aria-label={t('equipment.workbench.filters.regionAria', {
        defaultValue: 'Equipment filters',
      })}
      onSubmit={(e) => e.preventDefault()}
    >
      <div className={styles.filterField}>
        <label htmlFor="eq-f-crit" className={styles.filterLabel}>
          {t('equipment.workbench.filters.criticality', {
            defaultValue: 'Criticality',
          })}
        </label>
        <select
          id="eq-f-crit"
          className={styles.filterSelect}
          value={value.criticality ?? ''}
          onChange={(e) =>
            update('criticality', e.target.value as EquipmentCriticality | '')
          }
        >
          <option value="">
            {t('equipment.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {CRITICALITY.map((c) => (
            <option key={c} value={c}>
              {t(`equipment.criticality.${c}`, { defaultValue: c })}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label htmlFor="eq-f-gamp" className={styles.filterLabel}>
          {t('equipment.workbench.filters.gamp', {
            defaultValue: 'GAMP class',
          })}
        </label>
        <select
          id="eq-f-gamp"
          className={styles.filterSelect}
          value={value.gamp_class ?? ''}
          onChange={(e) => update('gamp_class', e.target.value as GampClass | '')}
        >
          <option value="">
            {t('equipment.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {GAMP.map((g) => (
            <option key={g} value={g}>
              {t(`equipment.gamp.${g}`, { defaultValue: g })}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label htmlFor="eq-f-life" className={styles.filterLabel}>
          {t('equipment.workbench.filters.lifecycle', {
            defaultValue: 'Lifecycle status',
          })}
        </label>
        <select
          id="eq-f-life"
          className={styles.filterSelect}
          value={value.lifecycle_status ?? ''}
          onChange={(e) =>
            update(
              'lifecycle_status',
              e.target.value as EquipmentLifecycleStatus | '',
            )
          }
        >
          <option value="">
            {t('equipment.workbench.filters.all', { defaultValue: 'All' })}
          </option>
          {LIFECYCLE.map((s) => (
            <option key={s} value={s}>
              {t(`equipment.lifecycle.${s}`, { defaultValue: s })}
            </option>
          ))}
        </select>
      </div>

      <div className={styles.filterField}>
        <label htmlFor="eq-f-site" className={styles.filterLabel}>
          {t('equipment.workbench.filters.site', { defaultValue: 'Site' })}
        </label>
        <input
          id="eq-f-site"
          className={styles.filterInput}
          value={value.site_code ?? ''}
          onChange={(e) => update('site_code', e.target.value)}
          placeholder={t('equipment.workbench.filters.sitePlaceholder', {
            defaultValue: 'Site code (e.g. CAI-01)',
          })}
        />
      </div>

      {hasAny && (
        <button
          type="button"
          className={styles.filterClear}
          onClick={clear}
          aria-label={t('equipment.workbench.filters.clearAria', {
            defaultValue: 'Clear all equipment filters',
          })}
        >
          {t('equipment.workbench.filters.clear', { defaultValue: 'Clear' })}
        </button>
      )}
    </form>
  );
}
